import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Button from '@/components/base/Button';
import Card from '@/components/base/Card';
import ConfirmDialog from '@/components/base/ConfirmDialog';
import ErrorState from '@/components/base/ErrorState';
import Skeleton from '@/components/base/Skeleton';
import EmptyState from '@/components/base/EmptyState';
import Toast from '@/components/base/Toast';
import { systemSettings } from '@/mocks/settings';
import {
  deleteDriver,
  fetchDriver,
  fetchDriverTasks,
  resetDriverPassword,
  setDriverDisabled,
  updateDriver,
} from '@/services/driversService';
import type {
  Driver,
  DriverDocumentKey,
  DriverProfilePayload,
  DriverTask,
} from '@/types';
import DriverFormModal from '@/pages/dashboard/drivers/components/DriverFormModal';
import ResetPasswordModal from '@/pages/dashboard/drivers/components/ResetPasswordModal';
import DocumentPreviewModal from '@/pages/dashboard/drivers/components/DocumentPreviewModal';
import { AccountDisabledBadge, AvailabilityBadge } from '@/pages/dashboard/drivers/components/DriverStatusBadges';
import DriverDocumentsSection from '@/pages/dashboard/drivers/detail/components/DriverDocumentsSection';
import DriverTasksTable from '@/pages/dashboard/drivers/detail/components/DriverTasksTable';

type ConfirmState = { type: 'disable' | 'enable' | 'delete' };
type ToastState = { message: string; tone: 'success' | 'error' };

const DOCUMENT_LABEL_KEY: Record<DriverDocumentKey, string> = {
  nationalId: 'drivers.form.nationalId',
  license: 'drivers.form.license',
  vehicle: 'drivers.form.vehicle',
};

export default function DriverDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { driverId } = useParams<{ driverId: string }>();

  const [driver, setDriver] = useState<Driver | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(false);

  const [tasks, setTasks] = useState<DriverTask[] | null>(null);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [tasksError, setTasksError] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [previewKey, setPreviewKey] = useState<DriverDocumentKey | null>(null);
  const [toast, setToast] = useState<ToastState | null>(null);

  const loadDriver = useCallback(async () => {
    if (!driverId) return;
    setLoading(true);
    setError(false);
    setNotFound(false);
    try {
      const data = await fetchDriver(driverId);
      setDriver(data);
    } catch (err) {
      if (err instanceof Error && err.message === 'driver-not-found') setNotFound(true);
      else setError(true);
    } finally {
      setLoading(false);
    }
  }, [driverId]);

  const loadTasks = useCallback(async () => {
    if (!driverId) return;
    setTasksLoading(true);
    setTasksError(false);
    try {
      setTasks(await fetchDriverTasks(driverId));
    } catch {
      setTasksError(true);
    } finally {
      setTasksLoading(false);
    }
  }, [driverId]);

  useEffect(() => {
    loadDriver();
    loadTasks();
  }, [loadDriver, loadTasks]);

  const notify = (message: string, tone: ToastState['tone'] = 'success') =>
    setToast({ message, tone });

  const handleEditSubmit = async (payload: DriverProfilePayload, id: string | null) => {
    if (!id) return;
    const updated = await updateDriver(id, payload);
    setDriver(updated);
    notify(t('drivers.toast.updated'));
  };

  const handleResetPassword = async (password: string) => {
    if (!driver) return;
    await resetDriverPassword(driver.id, password);
    notify(t('drivers.reset.success'));
  };

  const handleConfirm = async () => {
    if (!confirm || !driver) return;
    setConfirmLoading(true);
    try {
      if (confirm.type === 'delete') {
        await deleteDriver(driver.id);
        navigate('/dashboard/drivers', { state: { toast: t('drivers.toast.deleted') } });
        return;
      }
      const disabled = confirm.type === 'disable';
      await setDriverDisabled(driver.id, disabled);
      setDriver((prev) => (prev ? { ...prev, accountDisabled: disabled } : prev));
      notify(t(disabled ? 'drivers.toast.disabled' : 'drivers.toast.enabled'));
      setConfirm(null);
    } catch {
      notify(t('common.errorText'), 'error');
      setConfirm(null);
    } finally {
      setConfirmLoading(false);
    }
  };

  const backLink = (
    <Link
      to="/dashboard/drivers"
      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-900"
    >
      <i className="ri-arrow-left-line text-lg rtl:rotate-180" aria-hidden="true" />
      {t('drivers.detail.back')}
    </Link>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-56" />
            </div>
          </div>
          <Skeleton className="h-24 w-full" />
        </Card>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <EmptyState
            icon="ri-user-unfollow-line"
            title={t('drivers.notFound')}
            action={
              <Button variant="primary" size="md" onClick={() => navigate('/dashboard/drivers')}>
                {t('drivers.notFoundBack')}
              </Button>
            }
          />
        </Card>
      </div>
    );
  }

  if (error || !driver) {
    return (
      <div className="space-y-5">
        {backLink}
        <Card>
          <ErrorState
            title={t('drivers.errorDetail')}
            description={t('common.errorText')}
            retryLabel={t('common.retry')}
            onRetry={loadDriver}
          />
        </Card>
      </div>
    );
  }

  const previewDoc = previewKey ? driver.documents[previewKey] : null;

  return (
    <div className="space-y-5">
      {backLink}

      <header className="flex flex-wrap items-center gap-4">
        <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-500 text-xl font-bold text-background-50">
          {driver.avatarUrl ? (
            <img src={driver.avatarUrl} alt={driver.fullName} className="h-full w-full object-cover" />
          ) : (
            driver.fullName.charAt(0)
          )}
        </span>
        <div className="min-w-0">
          <h1 className="text-lg font-bold text-foreground-950 md:text-xl">{driver.fullName}</h1>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <AvailabilityBadge value={driver.availability} />
            {driver.accountDisabled ? <AccountDisabledBadge /> : null}
          </div>
        </div>
      </header>

      {driver.accountDisabled ? (
        <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          <i className="ri-lock-line mt-0.5 text-lg text-amber-600" aria-hidden="true" />
          {t('drivers.detail.disabledNotice')}
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <h2 className="mb-4 text-base font-semibold text-foreground-950">
            {t('drivers.detail.accountSummary')}
          </h2>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-foreground-500">{t('drivers.form.fullName')}</dt>
              <dd className="mt-1 text-sm font-medium text-foreground-900">{driver.fullName}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">{t('drivers.form.phone')}</dt>
              <dd className="lb-ltr-field mt-1 text-sm font-medium text-foreground-900">{driver.phone}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">{t('drivers.form.username')}</dt>
              <dd className="lb-ltr-field mt-1 text-sm font-medium text-foreground-900">@{driver.username}</dd>
            </div>
            <div>
              <dt className="text-xs text-foreground-500">{t('drivers.table.availability')}</dt>
              <dd className="mt-1">
                <AvailabilityBadge value={driver.availability} />
              </dd>
            </div>
          </dl>
        </Card>

        <Card>
          <h2 className="mb-4 text-base font-semibold text-foreground-950">{t('drivers.detail.actions')}</h2>
          <div className="flex flex-col gap-2.5">
            <Button
              variant="outline"
              size="md"
              fullWidth
              icon="ri-pencil-line"
              onClick={() => setEditOpen(true)}
            >
              {t('drivers.action.edit')}
            </Button>
            <Button
              variant="outline"
              size="md"
              fullWidth
              icon="ri-lock-password-line"
              onClick={() => setResetOpen(true)}
            >
              {t('drivers.action.resetPassword')}
            </Button>
            {driver.accountDisabled ? (
              <Button
                variant="secondary"
                size="md"
                fullWidth
                icon="ri-restart-line"
                onClick={() => setConfirm({ type: 'enable' })}
              >
                {t('drivers.action.enable')}
              </Button>
            ) : (
              <Button
                variant="outline"
                size="md"
                fullWidth
                icon="ri-lock-line"
                onClick={() => setConfirm({ type: 'disable' })}
              >
                {t('drivers.action.disable')}
              </Button>
            )}
            <div className="mt-1 border-t border-background-100 pt-3">
              <Button
                variant="danger-outline"
                size="md"
                fullWidth
                icon="ri-delete-bin-line"
                onClick={() => setConfirm({ type: 'delete' })}
              >
                {t('drivers.action.delete')}
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <Card padded={false} className="overflow-hidden">
        <div className="border-b border-background-200 px-4 py-4 md:px-5">
          <h2 className="text-base font-semibold text-foreground-950">{t('drivers.detail.documents')}</h2>
          <p className="mt-0.5 text-xs text-foreground-500">{t('drivers.detail.documentsNote')}</p>
        </div>
        <DriverDocumentsSection documents={driver.documents} onView={(key) => setPreviewKey(key)} />
      </Card>

      <Card padded={false} className="overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-background-200 px-4 py-4 md:px-5">
          <div>
            <h2 className="text-base font-semibold text-foreground-950">{t('drivers.detail.tasks')}</h2>
            <p className="mt-0.5 text-xs text-foreground-500">{t('drivers.detail.tasks.subtitle')}</p>
          </div>
        </div>

        {tasksLoading ? (
          <div className="space-y-3 p-4 md:p-5">
            {[0, 1, 2].map((i) => (
              <div key={i} className="flex items-center gap-4">
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 flex-1" />
                <Skeleton className="h-5 w-20" />
              </div>
            ))}
          </div>
        ) : tasksError ? (
          <div className="p-4 md:p-5">
            <ErrorState
              title={t('drivers.detail.tasks.error')}
              description={t('common.errorText')}
              retryLabel={t('common.retry')}
              onRetry={loadTasks}
            />
          </div>
        ) : tasks && tasks.length > 0 ? (
          <DriverTasksTable tasks={tasks} currency={systemSettings.currency} />
        ) : (
          <div className="p-4 md:p-5">
            <EmptyState icon="ri-history-line" title={t('drivers.detail.tasks.empty')} />
          </div>
        )}

        {!tasksLoading && !tasksError && tasks && tasks.length > 0 ? (
          <div className="border-t border-background-100 bg-background-100/60 px-4 py-3 md:px-5">
            <p className="flex items-start gap-2 text-xs leading-relaxed text-foreground-500">
              <i className="ri-information-line mt-0.5 text-base text-foreground-400" aria-hidden="true" />
              {t('drivers.detail.tasks.note')}
            </p>
          </div>
        ) : null}
      </Card>

      <DriverFormModal
        open={editOpen}
        driver={driver}
        onClose={() => setEditOpen(false)}
        onSubmit={handleEditSubmit}
      />

      <ResetPasswordModal
        open={resetOpen}
        driver={driver}
        onClose={() => setResetOpen(false)}
        onSubmit={handleResetPassword}
      />

      <DocumentPreviewModal
        open={previewKey !== null}
        title={previewKey ? t(DOCUMENT_LABEL_KEY[previewKey]) : t('drivers.detail.document.title')}
        document={previewDoc}
        onClose={() => setPreviewKey(null)}
      />

      <ConfirmDialog
        open={confirm?.type === 'disable'}
        tone="warning"
        icon="ri-lock-line"
        title={t('drivers.confirm.disable.title')}
        text={t('drivers.confirm.disable.text')}
        confirmLabel={t('drivers.confirm.disable.confirm')}
        cancelLabel={t('drivers.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm?.type === 'enable'}
        tone="primary"
        icon="ri-restart-line"
        title={t('drivers.action.enable')}
        text={t('drivers.detail.disabledNotice')}
        confirmLabel={t('drivers.action.enable')}
        cancelLabel={t('drivers.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      <ConfirmDialog
        open={confirm?.type === 'delete'}
        tone="danger"
        title={t('drivers.confirm.delete.title')}
        text={t('drivers.confirm.delete.text')}
        confirmLabel={t('drivers.confirm.delete.confirm')}
        cancelLabel={t('drivers.confirm.cancel')}
        loading={confirmLoading}
        onConfirm={handleConfirm}
        onClose={() => (confirmLoading ? undefined : setConfirm(null))}
      />

      {toast ? <Toast message={toast.message} tone={toast.tone} onClose={() => setToast(null)} /> : null}
    </div>
  );
}